import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LuArrowRight, LuLock } from "react-icons/lu";

function FeaturedProducts({ title, products, dark = false }) {
  const { user } = useAuth();

  return (
    <section className={`py-24 px-6 ${dark ? "bg-brand-dark-high" : "bg-brand-surface"}`}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="label-overline block mb-2">
              {dark ? "Best Sellers" : "New Arrivals"}
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-brand-navy">{title}</h2>
            <p className="text-brand-muted mt-1 text-sm">
              {dark
                ? "Top-moving products across all categories."
                : "Fresh inventory recently landed in our warehouses."}
            </p>
          </div>
          <span className="badge-wholesale">Wholesale Only</span>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((product, idx) => (
            <div
              key={product.id}
              className="group bg-white rounded-2xl border border-slate-100 shadow-xs hover:border-brand-amber/25 hover:shadow-blue transition-all duration-300 overflow-hidden"
            >
              {/* Image */}
              <Link to={`/products/details/${product.id}`} className="block">
                <div className="relative aspect-[4/5] overflow-hidden bg-brand-surface">
                  {idx === 0 && (
                    <span className="absolute top-3 left-3 z-10 bg-brand-amber text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-xs">
                      Top Pick
                    </span>
                  )}
                  {idx === 1 && (
                    <span className="absolute top-3 left-3 z-10 bg-brand-orange text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-xs">
                      New
                    </span>
                  )}
                  <img
                    src={product.imageUrls?.[0] || "/images/pictureComingSoon.jpg"}
                    alt={product.name || "Product"}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                    loading="lazy"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/4 transition-colors duration-300" />
                </div>
              </Link>

              {/* Info */}
              <div className="p-4 space-y-1">
                {product.category && (
                  <p className="label-overline text-[10px]">{product.category}</p>
                )}
                <Link to={`/products/details/${product.id}`}>
                  <h4 className="text-sm font-bold text-slate-900 hover:text-brand-amber transition-colors line-clamp-2 leading-snug">
                    {product.name}
                  </h4>
                </Link>

                {/* Bottom row */}
                <div className="flex items-center justify-between pt-3 mt-1 border-t border-slate-100">
                  {user ? (
                    <div>
                      <p className="text-slate-900 font-black text-sm">
                        ${product.price?.toFixed(2)}
                      </p>
                      <p className="text-slate-400 text-[10px] font-medium">per unit</p>
                    </div>
                  ) : (
                    <Link
                      to="/account"
                      className="flex items-center gap-1 text-[11px] text-brand-muted italic hover:text-brand-amber transition-colors"
                    >
                      <LuLock size={10} />
                      Login to view price
                    </Link>
                  )}
                  <Link
                    to={`/products/details/${product.id}`}
                    className="w-8 h-8 rounded-lg bg-brand-amber/10 hover:bg-brand-amber text-brand-amber hover:text-white flex items-center justify-center transition-all duration-200 cursor-pointer"
                    title="View product"
                  >
                    <LuArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturedProducts;
